import { defineReceiver } from '@drapejs/invoker';
import { fetchPage } from '@drapejs/core';
import { batch } from '@distancify/drapejs-litium';
import { gql } from 'graphql-request';
import {
  getCategoryHighlighetedProductsCacheKey,
  getCategoryCacheKey,
  getCategoryProductsCacheKey,
  getCategoryFacetsCacheKey,
  getFacetsFromQuery,
} from '@/composables/useDynamicProductListing';
import { isCategoryPage } from '@/helpers/page-type';



export const process = defineReceiver(fetchPage, async function (
  command,
  data,
) {
  if (!isCategoryPage(data)) {
    return data;
  }

  const categoryResult = await this.cache.getItem(
    getCategoryProductsCacheKey(command.path, command.query),
  );
  data.hits = categoryResult?.hits || 0;
  this.cache.setItem(command.cacheKey, data);

  return data;
});

export const schedule = defineReceiver(fetchPage, async function (
  command,
  data,
) {
  if (!isCategoryPage(data)) {
    return data;
  }

  const facets = getFacetsFromQuery(command.query);

  const commonVariables = {
    rootCategory: {
      value: data.dataJson?.category?.systemId || data.fields?.CategorySystemId?.id || '',
      type: 'String',
    },
    facets: {
      value: facets,
      type: '[SelectedFacetInput]',
    },
    searchPhrase: {
      value: decodeURIComponent(command.query?.search || ''),
      type: 'String',
    },
  };

  let highlightedProductIds = [];

  if(!commonVariables.facets.value.length && !commonVariables.searchPhrase.value && !command.query.sort){
    highlightedProductIds = (
      data.blocks?.ProductListingHighlights?.filter((b) => b.template === 'ProductListingHighlights')?.map(
        (b) => b.fields.ProductIDs || []
      ) || []
    ).reduce((acc, curr) => {
      return acc.concat(curr?.map((e) => e.id).filter(e=> !acc.find(ee => ee === e)));
    }, [])
    if(highlightedProductIds.length > 0) {
      await this.invoke(batch.scheduleQuery, {
        cacheKey: getCategoryHighlighetedProductsCacheKey(command.path, command.query),
        query: gql`
            productSearchV2(rootCategory: $rootCategory, take: $take, skip: $skip, sort: $sort, baseProductIds: $baseProductIds) {
            ${getProductsSearchFields()}
          }`,
        variables: {
          rootCategory: {
            value: data.dataJson?.category?.systemId || data.fields?.CategorySystemId?.id || '',
            type: 'String',
          },
          skip: {
            value: 0,
            type: 'Int',
          },
          take: {
            value: 9999,
            type: 'Int',
          },
          sort: {
            value: 'noSorting',
            type: 'String',
          },
          baseProductIds: {
            value: highlightedProductIds,
            type: '[Guid]!',
          }
        },
      });
    }
    else{
      await this.cache.setItem(getCategoryHighlighetedProductsCacheKey(command.path, command.query), { hits: 0, product: []});
    }
  }


  await this.invoke(batch.scheduleQuery, {
    cacheKey: getCategoryCacheKey(command.path),
    query: gql`
      categorySearch(category: $category){
          hits
          categories {
            categorySystemId
            name
            url
            parents {
              name
              url
            }
            children(take: $take) {
              name
              url
            }
          }
      }`,
    variables: {
      category: {
        value: data.dataJson?.category?.systemId || data.fields?.CategorySystemId?.id || '',
        type: 'String',
      },
      take: {
        value: data.dataJson?.category?.fields?.NumberOfSubcategoriesToDisplay || 8,
        type: 'Int'
      }
    },
  });

  await this.invoke(batch.scheduleQuery, {
    cacheKey: getCategoryFacetsCacheKey(command.path, command.query),
    query: gql`
productFields(rootCategory: $rootCategory, facets: $facets, searchPhrase: $searchPhrase){
    id
    type
    name
    values {
      count
      label
      value
    }
    selectedValues {
      count
      label
      value
      value
    }
    range {
      min
      max
    }
    selectedRange {
      min
      max
    }
}
  `,
    variables: {
      ...commonVariables,
    },
  });

  await this.invoke(batch.scheduleQuery, {
    cacheKey: getCategoryProductsCacheKey(command.path, command.query),
    query: gql`
        productSearchV2(rootCategory: $rootCategory, take: $take, skip: $skip, facets: $facets, sort: $sort, searchPhrase: $searchPhrase, excludedBaseProductIds: $excludedBaseProductIds) {
        ${getProductsSearchFields()}
      }`,
    variables: {
      skip: {
        value: 0,
        type: 'Int',
      },
      take: {
        value: 9999,
        type: 'Int',
      },
      sort: {
        value: command.query.sort || 'relevance',
        type: 'String',
      },
      excludedBaseProductIds: {
        value: highlightedProductIds,
        type: '[Guid]!',
      },
      ...commonVariables,
    },
  });

  return data;
});

export function getProductsSearchFields(): string {
  return `
    hits
    products {
      ${getProductFields()}
    }`;
}

export function getProductFields(): string {
  return `
        articleNumber
        productName
        url
        price
        campaignPrice
        campaignName
        image
        variants {
          articleNumber: id
        }
        modelNumber
        colorName
        imageIds
        colorCode
        allColorCodes
        colorOptions {
            url
            imageId
            colorCode
            sizes {
                caption
                inStock
              }
        }
        sizes {
          caption
          inStock
        }
        productCardTag {
          name
          value
        }
  `;
}
